import * as Sentry from '@sentry/react';

import type {
  ChatMessage,
  ChatMessageContent,
  DocumentTagID,
  VoiceAttachment,
} from '@karehero/models';
import { summariseLevels } from '@karehero/helpers/audio';
import { randomUUID } from '@karehero/helpers/string';

import { useSubmitTextCaptureMutation } from 'store/api/inference';
import { textMessage } from 'utils/chatMessages';
import {
  useDocumentUpload,
  type LiveRecording,
  type UploadDocumentOptions,
} from './useDocumentUpload';
import type {
  MessageRequest,
  SentMessage,
  UpdateMessage,
} from './useMessageOutbox';

const VOICE_LEVEL_COUNT = 64;

/** Marks audio in its upload context as already read from its live transcript. */
const TRANSCRIBED_LIVE = 'transcribedLive';

export interface UseChatSenderOptions {
  careCircleId?: string;
  /** Where what they say is from, as it is sent to be read. */
  source: string;
  /** The tag their files are kept under. */
  documentTag: DocumentTagID;
  /** What they are answering, sent with what they say so it is read in context. */
  context: string;
  /** Whether what they say may be read, or only kept. */
  canRead: boolean;
  persistMessage: (message: ChatMessage) => void;
  updateMessage: UpdateMessage;
  /** Gets every message, with no request where it is only kept. */
  sendMessage: (
    messageID: string,
    request: MessageRequest | null,
    message: SentMessage,
  ) => void;
}

/**
 * useChatSender puts what they say, record or attach in the thread and has it
 * read: text and transcripts straight away, files once uploaded. Each send
 * gives back the id of the message it became.
 */
export const useChatSender = ({
  careCircleId,
  source,
  documentTag,
  context,
  canRead,
  persistMessage,
  updateMessage,
  sendMessage,
}: UseChatSenderOptions) => {
  const [submitTextCapture] = useSubmitTextCaptureMutation();
  const { uploadDocument, uploadRecording } = useDocumentUpload(
    careCircleId ?? '',
  );

  // Thrown inside a send, so a message sent without one shows as not sent.
  const requireCareCircle = () => {
    if (!careCircleId) throw new Error('No care circle to send to');
    return careCircleId;
  };

  /** Uploads the file, or keeps the recording the API already holds of it. */
  const uploadFile = async (
    file: File,
    options: UploadDocumentOptions,
    recording: LiveRecording | null = null,
  ) => {
    requireCareCircle();
    return recording
      ? uploadRecording(file, recording, options)
      : uploadDocument(file, options);
  };

  /** Has text read straight away, without a file to read it from first. */
  const capture =
    (
      messageID: string,
      text: string,
      messageKind: 'text' | 'voice',
      then?: () => void,
    ): MessageRequest =>
    async () => {
      await submitTextCapture({
        careCircleId: requireCareCircle(),
        body: { text, messageId: messageID, context, source, messageKind },
      }).unwrap();
      then?.();
      return undefined;
    };

  /** Has text read on its own, apart from any message: best effort. */
  const captureText = async (
    text: string,
    messageID: string,
    askedAs: string,
  ) => {
    try {
      await submitTextCapture({
        careCircleId: requireCareCircle(),
        body: { text, messageId: messageID, context: askedAs, source },
      }).unwrap();
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // The audio is kept marked as read from its transcript, so it is not read
  // again, and as no submission, so the summary is not asked for again.
  const uploadTranscribedAudio = (
    file: File,
    recording: LiveRecording | null,
  ) =>
    uploadFile(
      file,
      {
        tags: [documentTag],
        context: { question: context, [TRANSCRIBED_LIVE]: 'true' },
      },
      recording,
    );

  const keepAudio = async (
    messageID: string,
    file: File,
    recording: LiveRecording | null,
  ) => {
    try {
      const documentId = await uploadTranscribedAudio(file, recording);
      updateMessage(messageID, { status: 'sent', content: { documentId } });
    } catch (err) {
      // The transcript is what is read; the audio is kept for the record.
      Sentry.captureException(err);
    }
  };

  const sendText = (text: string) => {
    const msg = textMessage('user', text);
    persistMessage(msg);
    sendMessage(msg.id, canRead ? capture(msg.id, text, 'text') : null, {
      kind: 'text',
      words: text,
    });
    return msg.id;
  };

  /** A voice note read from its transcript; its audio is kept once that is in. */
  const sendTranscript = (
    file: File,
    content: VoiceAttachment,
    transcript: string,
    recording: LiveRecording | null,
  ) => {
    const msg: ChatMessage = {
      id: randomUUID(),
      from: 'user',
      content: { ...content, transcript },
      status: 'sending',
    };
    persistMessage(msg);
    if (canRead) {
      sendMessage(
        msg.id,
        capture(msg.id, transcript, 'voice', () =>
          keepAudio(msg.id, file, recording),
        ),
        { kind: 'voice', words: transcript },
      );
    } else {
      // Nothing may be read: the audio is only kept, and sent once it is.
      sendMessage(
        msg.id,
        async () => ({
          documentId: await uploadTranscribedAudio(file, recording),
        }),
        { kind: 'voice' },
      );
    }
    return msg.id;
  };

  const upload = (
    file: File,
    content: ChatMessageContent,
    recording: LiveRecording | null = null,
  ) => {
    const msg: ChatMessage = {
      id: randomUUID(),
      from: 'user',
      content,
      status: 'sending',
    };
    persistMessage(msg);
    sendMessage(
      msg.id,
      async () => ({
        documentId: await uploadFile(
          file,
          { tags: [documentTag], context: { question: context } },
          recording,
        ),
      }),
      { kind: content.kind === 'voice' ? 'voice' : 'file' },
    );
    return msg.id;
  };

  /**
   * Read from its transcript when there is one worth reading, else from the
   * audio; kept from the recording the API holds when there is one.
   */
  const sendVoiceNote = (
    file: File,
    durationSeconds: number,
    levels: readonly number[],
    transcript: string | null,
    recordingId: string | null = null,
  ) => {
    const content: VoiceAttachment = {
      kind: 'voice',
      durationSeconds,
      levels: levels.length
        ? summariseLevels(levels, VOICE_LEVEL_COUNT)
        : undefined,
    };
    const recording = recordingId ? { id: recordingId, durationSeconds } : null;
    return transcript
      ? sendTranscript(file, content, transcript, recording)
      : upload(file, content, recording);
  };

  const sendFiles = (files: File[]) =>
    files.map((file) =>
      upload(file, {
        kind: 'file',
        name: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }),
    );

  return { sendText, sendVoiceNote, sendFiles, captureText };
};
